/**
 * Journey Rooms — Design Mode (v2)
 * -----------------------------------------------------------------------------
 * Persistent, structured layout store for the in-app placement tool. Supports:
 *   • Offsets on built-in decorative elements (--dx / --dy / --rot / --scl)
 *   • Hiding built-in elements (they render `display: none` but stay in the DOM)
 *   • Adding *custom* elements — votives / vessels / rugs — placed freely
 *     inside the hallway canvas via absolute x / y (px).
 *
 * The Copy Layout JSON dumps this whole schema so the next iteration can bake
 * user placements back into the base React + CSS as the new architectural
 * defaults.
 */
import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "journeyRoomsDesignLayout.v3";
const listeners = new Set();

const emptyState = () => ({ offsets: {}, hidden: {}, custom: [] });

function loadState() {
  if (typeof window === "undefined") return emptyState();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw);
    return {
      offsets: parsed.offsets || {},
      hidden: parsed.hidden || {},
      custom: Array.isArray(parsed.custom) ? parsed.custom : [],
    };
  } catch {
    return emptyState();
  }
}

let state = loadState();

function saveState() {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota errors */
  }
  listeners.forEach((l) => l(state));
}

// ── Reads ────────────────────────────────────────────────────────────────
export function getLayout() {
  return state;
}
export function getOffset(id) {
  return state.offsets[id] || {};
}
export function isHidden(id) {
  return Boolean(state.hidden[id]);
}
export function getCustomById(id) {
  return state.custom.find((c) => c.id === id);
}

// ── Built-in offsets ─────────────────────────────────────────────────────
export function setOffset(id, patch) {
  const merged = { ...(state.offsets[id] || {}), ...patch };
  const cleaned = {};
  if (Math.round(merged.dx || 0) !== 0) cleaned.dx = Math.round(merged.dx);
  if (Math.round(merged.dy || 0) !== 0) cleaned.dy = Math.round(merged.dy);
  if (Math.round(merged.rot || 0) !== 0) cleaned.rot = Math.round(merged.rot);
  if (merged.scl !== undefined && merged.scl !== 1) cleaned.scl = Number(merged.scl.toFixed(2));
  const nextOffsets = { ...state.offsets };
  if (Object.keys(cleaned).length === 0) delete nextOffsets[id];
  else nextOffsets[id] = cleaned;
  state = { ...state, offsets: nextOffsets };
  saveState();
}
export function nudge(id, ddx, ddy) {
  const cur = state.offsets[id] || {};
  setOffset(id, { dx: (cur.dx || 0) + ddx, dy: (cur.dy || 0) + ddy });
}
export function rotateBy(id, drot) {
  const cur = state.offsets[id] || {};
  setOffset(id, { rot: (cur.rot || 0) + drot });
}
export function resetOne(id) {
  const { [id]: _, ...rest } = state.offsets;
  state = { ...state, offsets: rest };
  saveState();
}

// ── Hide / restore built-ins ─────────────────────────────────────────────
export function setHidden(id, v) {
  const hidden = { ...state.hidden };
  if (v) hidden[id] = true;
  else delete hidden[id];
  state = { ...state, hidden };
  saveState();
}
export function restoreAllHidden() {
  state = { ...state, hidden: {} };
  saveState();
}

// ── Custom elements (freely-placed additions) ────────────────────────────
const KINDS = ["votive", "vessel", "rug"];
function newId() {
  return `custom-${Math.random().toString(36).slice(2, 8)}`;
}
export function addCustom({ kind, x, y }) {
  if (!KINDS.includes(kind)) throw new Error(`unknown kind: ${kind}`);
  const item = {
    id: newId(),
    kind,
    x: Math.round(x),
    y: Math.round(y),
    rot: 0,
    scl: 1,
    // rugs need an explicit width/height since flex sizing won't apply when
    // they're absolutely positioned. Votives & vessels use the class defaults.
    ...(kind === "rug" ? { w: 100, h: 60 } : {}),
  };
  state = { ...state, custom: [...state.custom, item] };
  saveState();
  return item.id;
}
export function updateCustom(id, patch) {
  const custom = state.custom.map((c) => (c.id === id ? { ...c, ...patch } : c));
  state = { ...state, custom };
  saveState();
}
export function nudgeCustom(id, ddx, ddy) {
  const c = state.custom.find((x) => x.id === id);
  if (!c) return;
  updateCustom(id, { x: Math.round(c.x + ddx), y: Math.round(c.y + ddy) });
}
export function rotateCustomBy(id, drot) {
  const c = state.custom.find((x) => x.id === id);
  if (!c) return;
  updateCustom(id, { rot: Math.round((c.rot || 0) + drot) });
}
export function removeCustom(id) {
  state = { ...state, custom: state.custom.filter((c) => c.id !== id) };
  saveState();
}

// ── Unified delete: hides built-ins, removes custom ──────────────────────
export function deleteElement(id) {
  if (id.startsWith("custom-")) removeCustom(id);
  else setHidden(id, true);
}
export function isCustomId(id) {
  return id.startsWith("custom-");
}

// ── Bulk reset ───────────────────────────────────────────────────────────
export function resetAll() {
  state = emptyState();
  saveState();
}

// ── Subscription / URL ───────────────────────────────────────────────────
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function isDesignModeActive() {
  if (typeof window === "undefined") return false;
  const p = new URLSearchParams(window.location.search);
  const v = p.get("design");
  return v === "1" || v === "true";
}

/**
 * React hook. Returns:
 *   active   → whether ?design=1 is in the URL
 *   layout   → the full layout state
 *   styleFor → (id) => inline style projecting --dx/--dy/--rot/--scl for built-ins
 *              or absolute left/top/transform for custom elements
 *   isHidden → (id) => boolean
 */
export function useDesignMode() {
  const [active, setActive] = useState(isDesignModeActive());
  const [layout, setLayout] = useState(state);

  useEffect(() => {
    const onLoc = () => setActive(isDesignModeActive());
    window.addEventListener("popstate", onLoc);
    return () => window.removeEventListener("popstate", onLoc);
  }, []);

  useEffect(() => subscribe(setLayout), []);

  const styleFor = useCallback(
    (id) => {
      const o = layout.offsets[id];
      if (!o) return undefined;
      const s = {};
      if (o.dx) s["--dx"] = `${o.dx}px`;
      if (o.dy) s["--dy"] = `${o.dy}px`;
      if (o.rot) s["--rot"] = `${o.rot}deg`;
      if (o.scl && o.scl !== 1) s["--scl"] = String(o.scl);
      return s;
    },
    [layout],
  );

  const styleForCustom = useCallback((item) => {
    const s = {
      position: "absolute",
      left: `${item.x}px`,
      top: `${item.y}px`,
      transform: `translate(-50%, -50%) rotate(${item.rot || 0}deg) scale(${item.scl || 1})`,
    };
    if (item.kind === "rug") {
      s.width = `${item.w || 100}px`;
      s.height = `${item.h || 60}px`;
      // rugs use flex sizing by default; override transform to skip the -50%
      // translate so left/top mark the top-left corner (feels more natural
      // for a rectangle than a point-centered anchor).
      s.transform = `rotate(${item.rot || 0}deg) scale(${item.scl || 1})`;
    }
    return s;
  }, []);

  const isElHidden = useCallback((id) => Boolean(layout.hidden[id]), [layout]);

  return { active, layout, styleFor, styleForCustom, isHidden: isElHidden };
}
